import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, CheckCircle2, AlertCircle, PenLine, RotateCcw, Mail, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = 'https://customer-assets.emergentagent.com/job_c4p-quoting-hub/artifacts/dnrg8j6i_C4P%20Transparent.png';
const NAVY = '#1e3a5f';
const ORANGE = '#ea580c';

const PRODUCT_LABELS = { STANDARD: 'C4P Standard', ADVANCED: 'C4P Advanced', MTPRO: 'C4P MTPro' };
const fmt = (n, sym) => `${sym}${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

// ── Signature fonts ──────────────────────────────────────────────────────────
const SIGNATURE_FONTS = [
  { id: 'dancing',  label: 'Elegant',  family: 'Dancing Script',  size: 52 },
  { id: 'greatvibes', label: 'Classic', family: 'Great Vibes',    size: 56 },
  { id: 'satisfy',  label: 'Casual',   family: 'Satisfy',         size: 48 },
  { id: 'pacifico', label: 'Bold',     family: 'Pacifico',        size: 44 },
];

function loadSignatureFonts() {
  if (document.getElementById('sig-fonts')) return;
  const link = document.createElement('link');
  link.id   = 'sig-fonts';
  link.rel  = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Satisfy&family=Pacifico&display=swap';
  document.head.appendChild(link);
}

function renderTextToCanvas(name, fontFamily, fontSize) {
  const canvas = document.createElement('canvas');
  canvas.width  = 460;
  canvas.height = 140;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.font = `${fontSize}px "${fontFamily}"`;
  ctx.fillStyle = NAVY;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name || '', canvas.width / 2, canvas.height / 2);
  return canvas.toDataURL('image/png');
}

// ── Draw signature canvas ────────────────────────────────────────────────────
function SignatureCanvas({ onChange }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = useCallback((e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, []);

  const draw = useCallback((e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = NAVY;
    const { x, y } = getPos(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
  }, []);

  const end = useCallback(() => {
    drawing.current = false;
    setHasDrawn(true);
    onChange(canvasRef.current.toDataURL('image/png'));
  }, [onChange]);

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onChange('');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={460} height={140}
        className="w-full rounded-lg border-2 touch-none cursor-crosshair"
        style={{ borderColor: hasDrawn ? NAVY : '#e2e8f0', background: '#fafafa' }}
        onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
      />
      {hasDrawn && (
        <button onClick={clear} className="flex items-center gap-1 mt-1.5 text-xs"
          style={{ color: '#94a3b8' }}>
          <RotateCcw size={11} /> Clear signature
        </button>
      )}
      {!hasDrawn && (
        <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
          Draw your signature above using your mouse or touchscreen
        </p>
      )}
    </div>
  );
}

// ── Created (typed) signature picker ─────────────────────────────────────────
function SignatureCreator({ name, selectedFont, onSelect }) {
  const [fontsReady, setFontsReady] = useState(false);

  useEffect(() => {
    loadSignatureFonts();
    // Wait for fonts to load before showing previews
    document.fonts.ready.then(() => setFontsReady(true));
  }, []);

  return (
    <div className="space-y-2">
      {!fontsReady ? (
        <div className="flex items-center gap-2 py-4 justify-center" style={{ color: '#94a3b8' }}>
          <Loader2 size={14} className="animate-spin" /> Loading fonts…
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {SIGNATURE_FONTS.map(font => (
            <button
              key={font.id}
              type="button"
              onClick={() => onSelect(font)}
              className="rounded-xl border-2 p-3 text-center transition-all hover:scale-[1.02]"
              style={{
                borderColor: selectedFont?.id === font.id ? NAVY : '#e2e8f0',
                background: selectedFont?.id === font.id ? 'hsl(218 52% 11% / 0.05)' : 'white',
                minHeight: '72px',
              }}
            >
              <p style={{
                fontFamily: `"${font.family}", cursive`,
                fontSize: Math.min(font.size * 0.55, 28),
                color: NAVY,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {name || 'Your Name'}
              </p>
              <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>{font.label}</p>
            </button>
          ))}
        </div>
      )}
      {selectedFont && name && (
        <p className="text-xs" style={{ color: '#16a34a' }}>
          <CheckCircle2 size={11} className="inline mr-1" />
          &ldquo;{font_label(selectedFont)}&rdquo; style selected
        </p>
      )}
      {!name && (
        <p className="text-xs" style={{ color: '#94a3b8' }}>
          Enter your full name above to preview signature styles
        </p>
      )}
    </div>
  );
}
const font_label = f => f?.label || '';

// ── Combined signature pad ────────────────────────────────────────────────────
function SignaturePad({ signerName, onChange }) {
  const [mode, setMode]           = useState('create'); // 'draw' | 'create'
  const [selectedFont, setSelectedFont] = useState(null);

  // Re-render generated sig whenever name or font changes
  useEffect(() => {
    if (mode === 'create' && selectedFont && signerName) {
      document.fonts.ready.then(() => {
        const img = renderTextToCanvas(signerName, selectedFont.family, selectedFont.size);
        onChange(img);
      });
    }
  }, [signerName, selectedFont, mode, onChange]);

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    onChange(''); // clear when switching modes
    setSelectedFont(null);
  };

  return (
    <div>
      {/* Mode toggle */}
      <div className="flex gap-1 mb-3 p-1 rounded-lg" style={{ background: '#f1f5f9' }}>
        {[
          { id: 'create', label: 'Create signature' },
          { id: 'draw',   label: 'Draw signature' },
        ].map(m => (
          <button
            key={m.id}
            type="button"
            onClick={() => handleModeSwitch(m.id)}
            className="flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-all"
            style={{
              background: mode === m.id ? 'white' : 'transparent',
              color: mode === m.id ? NAVY : '#94a3b8',
              boxShadow: mode === m.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'create' ? (
        <SignatureCreator
          name={signerName}
          selectedFont={selectedFont}
          onSelect={(font) => setSelectedFont(font)}
        />
      ) : (
        <SignatureCanvas onChange={onChange} />
      )}
    </div>
  );
}

// ── Step indicator ───────────────────────────────────────────────────────────
function StepDot({ n, active, done }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
        style={{
          background: done ? '#16a34a' : active ? NAVY : '#e2e8f0',
          color: done || active ? 'white' : '#94a3b8',
        }}>
        {done ? <CheckCircle2 size={16} /> : n}
      </div>
    </div>
  );
}

// ── Main SigningPage ─────────────────────────────────────────────────────────
// ── GDPR Privacy Notice — shown on all signing page steps ────────────────────
function GdprNotice({ controllerName }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-5 rounded-xl overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
        style={{ background: '#f8fafc', color: '#64748b' }}>
        <span className="text-xs font-medium flex items-center gap-1.5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Data Privacy Notice
        </span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div className="px-4 py-3 text-xs space-y-2" style={{ background: 'white', color: '#475569', borderTop: '1px solid #e2e8f0' }}>
          <p><strong style={{ color: '#334155' }}>Data Controller:</strong> {controllerName}</p>
          <p><strong style={{ color: '#334155' }}>What we collect:</strong> Your full name, email address, IP address, and the date and time you sign this document.</p>
          <p><strong style={{ color: '#334155' }}>Why we collect it:</strong> To form a legally binding record of your acceptance of this quotation, under contractual necessity (GDPR Article 6(1)(b)).</p>
          <p><strong style={{ color: '#334155' }}>How long we keep it:</strong> Signing records are retained for the duration of the contract plus 7 years to comply with legal and financial record-keeping obligations.</p>
          <p><strong style={{ color: '#334155' }}>Your rights:</strong> You have the right to access, correct, or request deletion of your personal data. Contact the data controller above to exercise these rights.</p>
          <p style={{ color: '#94a3b8' }}>Your data is processed and stored securely. We do not sell or share your personal data with third parties except as required to fulfil contractual obligations.</p>
        </div>
      )}
    </div>
  );
}

export default function SigningPage() {
  const { token } = useParams();
  const [step, setStep]           = useState('loading'); // loading|email|otp|sign|done|error
  const [quoteInfo, setQuoteInfo] = useState(null);
  const [errorMsg, setErrorMsg]   = useState('');
  const [busy, setBusy]           = useState(false);

  // Step 1 - email
  const [email, setEmail]         = useState('');
  // Step 2 - OTP
  const [otp, setOtp]             = useState('');
  const [signingSession, setSigningSession] = useState('');
  // Step 3 - sign
  const [signerName, setSignerName]   = useState('');
  const [signatureImg, setSignatureImg] = useState('');
  const [agreed, setAgreed]           = useState(false);
  const [poNumber, setPoNumber]       = useState('');
  const [result, setResult]           = useState(null);

  // Load quote info
  useEffect(() => {
    fetch(`${API}/api/sign/${token}`)
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (!ok) { setErrorMsg(data.detail || 'Invalid link'); setStep('error'); return; }
        setQuoteInfo(data);
        setEmail(data.recipient_email || '');
        setStep('email');
      })
      .catch(() => { setErrorMsg('Could not load quote. Please check your link.'); setStep('error'); });
  }, [token]);

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setErrorMsg(''); setBusy(true);
    try {
      const r = await fetch(`${API}/api/sign/${token}/request-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const d = await r.json();
      if (!r.ok) { setErrorMsg(d.detail || 'Failed to send code'); return; }
      setStep('otp');
    } finally { setBusy(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setErrorMsg(''); setBusy(true);
    try {
      const r = await fetch(`${API}/api/sign/${token}/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });
      const d = await r.json();
      if (!r.ok) { setErrorMsg(d.detail || 'Incorrect code'); return; }
      setSigningSession(d.signing_session);
      setStep('sign');
    } finally { setBusy(false); }
  };

  const handleSign = async (e) => {
    e.preventDefault();
    if (!signerName.trim()) { setErrorMsg('Please enter your full name'); return; }
    if (!signatureImg)       { setErrorMsg('Please draw your signature'); return; }
    if (!agreed)             { setErrorMsg('Please tick the acceptance checkbox'); return; }
    setErrorMsg(''); setBusy(true);
    try {
      const r = await fetch(`${API}/api/sign/${token}/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signing_session: signingSession, signer_name: signerName,
                               signature_image: signatureImg, agreed,
                               po_number: poNumber.trim() || null }),
      });
      const d = await r.json();
      if (!r.ok) { setErrorMsg(d.detail || 'Signing failed'); return; }
      setResult(d);
      setStep('done');
    } finally { setBusy(false); }
  };

  const stepNum = { email: 1, otp: 2, sign: 3, done: 4 };
  const curStep = stepNum[step] || 0;

  if (step === 'loading') return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#f1f5f9' }}>
      <Loader2 size={28} className="animate-spin" style={{ color: NAVY }} />
    </div>
  );

  if (step === 'error') return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: '#f1f5f9' }}>
      <img src={LOGO} alt="Cloud4Partners" className="h-10 mb-8 object-contain" />
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        <AlertCircle size={40} className="mx-auto mb-4" style={{ color: '#dc2626' }} />
        <h2 className="text-xl font-bold mb-2" style={{ color: NAVY }}>Link Unavailable</h2>
        <p className="text-sm" style={{ color: '#64748b' }}>{errorMsg}</p>
        <p className="text-xs mt-6" style={{ color: '#94a3b8' }}>Please contact your Cloud4Partners representative for assistance.</p>
      </div>
    </div>
  );

  if (step === 'done') return (
    <DonePage result={result} quoteInfo={quoteInfo} signerName={signerName} signatureImg={signatureImg} poNumber={poNumber} />
  );

  const sym = quoteInfo?.currency_symbol || '£';
  const total = quoteInfo?.billing_model === 'TENANT_DIRECT'
    ? quoteInfo?.tenant_invoice_monthly
    : quoteInfo?.monthly_total;

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: '#f1f5f9' }}>
      <div className="max-w-lg mx-auto">

        {/* Header */}
        <div className="text-center mb-8">
          <img src={LOGO} alt="Cloud4Partners" className="h-10 mx-auto mb-4 object-contain" />
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
            {quoteInfo?.is_change_request ? 'Contract Modification' : 'Quotation Acceptance'}
          </h1>
          {quoteInfo?.is_change_request && quoteInfo?.change_summary ? (
            <div className="mt-1 space-y-0.5">
              <p className="text-sm font-semibold" style={{ color: '#0e7490' }}>
                Change to {quoteInfo.change_summary.contract_number}
                {quoteInfo.change_summary.contract_name ? ` — ${quoteInfo.change_summary.contract_name}` : ''}
              </p>
              <p className="text-xs" style={{ color: '#64748b' }}>
                {quoteInfo?.quote_name || quoteInfo?.quote_number}
              </p>
            </div>
          ) : (
            <p className="text-sm mt-1" style={{ color: '#64748b' }}>
              {quoteInfo?.quote_name || quoteInfo?.quote_number}
            </p>
          )}
        </div>

        {/* Step indicator */}
        {curStep > 0 && (
          <div className="flex items-center justify-center gap-3 mb-8">
            {[1, 2, 3].map(n => (
              <React.Fragment key={n}>
                <StepDot n={n} active={curStep === n} done={curStep > n} />
                {n < 3 && <div className="h-px w-10" style={{ background: curStep > n ? '#16a34a' : '#e2e8f0' }} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Change request summary — shown when this is a contract modification */}
        {quoteInfo?.is_change_request && quoteInfo?.change_summary && (() => {
          const cs = quoteInfo.change_summary;
          const delta = cs.monthly_delta;
          const sym = quoteInfo?.currency_symbol || '£';
          const fmtAmt = (n) => `${sym}${Math.abs(n || 0).toFixed(2)}`;
          return (
            <div className="rounded-2xl border mb-5 overflow-hidden" style={{ borderColor: '#bae6fd', background: '#f0f9ff' }}>
              <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #bae6fd', background: '#e0f2fe' }}>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#0369a1' }}>Summary of Changes</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: NAVY }}>{cs.contract_number}{cs.contract_name ? ` — ${cs.contract_name}` : ''}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs" style={{ color: '#64748b' }}>Monthly change</p>
                  <p className="text-base font-bold" style={{ color: delta > 0 ? '#dc2626' : delta < 0 ? '#16a34a' : '#64748b' }}>
                    {delta > 0 ? '+' : delta < 0 ? '−' : ''}{fmtAmt(delta)}
                  </p>
                </div>
              </div>
              <div className="px-5 py-3 space-y-2">
                <div className="flex items-center justify-between text-xs" style={{ color: '#475569' }}>
                  <span>Previous monthly total</span>
                  <span className="font-mono font-semibold">{fmtAmt(cs.previous_monthly_total)}</span>
                </div>
                <div className="flex items-center justify-between text-xs font-bold" style={{ color: NAVY }}>
                  <span>New monthly total</span>
                  <span className="font-mono">{fmtAmt(cs.new_monthly_total)}</span>
                </div>
                {cs.changes.length > 0 && (
                  <div className="pt-2 space-y-1.5" style={{ borderTop: '1px solid #bae6fd' }}>
                    {cs.changes.map((ch, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="text-xs font-bold flex-shrink-0 w-4" style={{
                          color: ch.type === 'added' ? '#16a34a' : ch.type === 'removed' ? '#dc2626' : '#0369a1'
                        }}>
                          {ch.type === 'added' ? '+' : ch.type === 'removed' ? '−' : '↕'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium" style={{ color: NAVY }}>{ch.label}</p>
                          {ch.item_code && <p className="font-mono" style={{ color: '#94a3b8', fontSize: '10px' }}>{ch.item_code}</p>}
                          <p className="text-xs" style={{ color: '#64748b' }}>
                            {ch.type === 'added' && `Added — ${fmtAmt(ch.new_amount)}/mo`}
                            {ch.type === 'removed' && `Removed — was ${fmtAmt(ch.old_amount)}/mo`}
                            {ch.type === 'changed' && ch.old_qty !== ch.new_qty && ch.old_qty != null && ch.new_qty != null
                              ? `${ch.old_qty} → ${ch.new_qty} users · ${fmtAmt(ch.old_amount)} → ${fmtAmt(ch.new_amount)}/mo`
                              : ch.type === 'changed'
                                ? `${fmtAmt(ch.old_amount)} → ${fmtAmt(ch.new_amount)}/mo`
                                : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {cs.changes.length === 0 && (
                  <p className="text-xs pt-2" style={{ color: '#64748b', borderTop: '1px solid #bae6fd' }}>No individual line item changes — pricing rates may have been updated.</p>
                )}
              </div>
            </div>
          );
        })()}

        {/* Quote summary + full breakdown */}
        {quoteInfo && (
          <div className="bg-white rounded-2xl shadow-sm border mb-5 overflow-hidden">
            {/* Header row */}
            <div className="p-5" style={{ borderBottom: '1px solid #f1f5f9' }}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: '#94a3b8' }}>{quoteInfo?.is_change_request ? 'Contract Change' : 'Quotation'}</p>
                  <p className="font-mono font-bold text-lg" style={{ color: NAVY }}>{quoteInfo.quote_number}</p>
                  <p className="text-sm mt-1" style={{ color: '#475569' }}>
                    {quoteInfo.product_label || PRODUCT_LABELS[quoteInfo.product_code] || quoteInfo.product_code}
                    {' · '}{quoteInfo.num_users} users{' · '}{quoteInfo.term_months} months
                    {quoteInfo.region_name && <> · {quoteInfo.region_name}</>}
                  </p>
                  {quoteInfo.user_tier_label && (
                    <p className="text-xs mt-0.5 font-semibold" style={{ color: ORANGE }}>{quoteInfo.user_tier_label}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs" style={{ color: '#94a3b8' }}>Monthly Total</p>
                  <p className="text-2xl font-bold" style={{ color: ORANGE }}>{fmt(total, sym)}</p>
                  <p className="text-xs" style={{ color: '#94a3b8' }}>ex. {quoteInfo.currency === 'GBP' ? 'VAT' : 'tax'}</p>
                  {quoteInfo.currency === 'GBP' && total > 0 && (
                    <p className="text-xs mt-0.5 font-semibold" style={{ color: '#d97706' }}>
                      {sym}{(total * 1.20).toFixed(2)} inc. VAT
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Line items breakdown */}
            {quoteInfo.line_items && quoteInfo.line_items.length > 0 && (
              <div>
                <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <th className="text-right px-3 py-2 font-semibold" style={{ color: '#94a3b8', width: '36px' }}>Qty</th>
                      <th className="text-left px-3 py-2 font-semibold" style={{ color: '#94a3b8' }}>Description</th>
                      <th className="text-right px-3 py-2 font-semibold" style={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>Unit Price</th>
                      <th className="text-right px-3 py-2 font-semibold" style={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>Monthly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quoteInfo.line_items.map((l, i) => {
                      const qty = l.qty != null ? (l.qty === Math.floor(l.qty) ? Math.floor(l.qty) : l.qty) : '-';
                      const unitP = l.unit_price > 0 ? `${sym}${Number(l.unit_price).toFixed(2)}` : l.is_included ? '' : '-';
                      const monthly = l.is_included ? 'Included'
                        : l.monthly_amount > 0 ? `${sym}${Number(l.monthly_amount).toFixed(2)}`
                        : l.oneoff_amount > 0 ? `${sym}${Number(l.oneoff_amount).toFixed(2)}`
                        : '-';
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', opacity: l.is_included ? 0.65 : 1 }}>
                          <td className="text-right px-3 py-2" style={{ color: '#94a3b8' }}>{qty}</td>
                          <td className="px-3 py-2">
                            <p className={`font-semibold ${l.is_included ? 'text-xs' : ''}`} style={{ color: l.is_included ? '#94a3b8' : NAVY }}>{l.label}</p>
                            {l.sub_label && <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>{l.sub_label}</p>}
                            {l.item_code && <p className="font-mono mt-0.5" style={{ color: '#94a3b8', fontSize: '10px', opacity: 0.7 }}>{l.item_code}</p>}
                          </td>
                          <td className="text-right px-3 py-2 text-xs" style={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>{unitP}</td>
                          <td className="text-right px-3 py-2 font-bold" style={{ color: l.is_included ? '#94a3b8' : NAVY, whiteSpace: 'nowrap', fontSize: l.is_included ? '11px' : '12px' }}>{monthly}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: NAVY }}>
                      <td colSpan={3} className="px-3 py-2.5 text-right font-bold text-xs uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.7)' }}>
                        Monthly Total ({quoteInfo.currency === 'GBP' ? 'ex. VAT' : 'ex. tax'}) - {quoteInfo.currency}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold" style={{ color: 'white', whiteSpace: 'nowrap' }}>
                        {fmt(total, sym)}
                      </td>
                    </tr>
                    {quoteInfo.currency === 'GBP' && total > 0 && (
                      <>
                        <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                          <td colSpan={3} className="px-3 py-1.5 text-right text-xs font-semibold" style={{ color: '#d97706' }}>VAT @ 20%</td>
                          <td className="px-3 py-1.5 text-right text-xs font-semibold" style={{ color: '#d97706', whiteSpace: 'nowrap' }}>{sym}{(total * 0.20).toFixed(2)}</td>
                        </tr>
                        <tr style={{ background: '#fff7ed' }}>
                          <td colSpan={3} className="px-3 py-2 text-right text-xs font-bold">Monthly Total (inc. VAT)</td>
                          <td className="px-3 py-2 text-right text-xs font-bold" style={{ color: '#c2410c', whiteSpace: 'nowrap' }}>{sym}{(total * 1.20).toFixed(2)}</td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {errorMsg && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMsg}</AlertDescription>
          </Alert>
        )}

        {/* ── Step 1: Email ── */}
        {step === 'email' && (
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail size={18} style={{ color: NAVY }} />
              <h2 className="font-bold text-lg" style={{ color: NAVY }}>Verify your identity</h2>
            </div>
            <p className="text-sm mb-5" style={{ color: '#475569' }}>
              We'll send a one-time code to your email address to confirm your identity before you sign.
            </p>
            <form onSubmit={handleRequestOtp} className="space-y-4">
              <div>
                <label className="text-sm font-semibold block mb-1.5" style={{ color: NAVY }}>Email address</label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com" required
                  style={{ height: '44px' }} />
              </div>
              <Button type="submit" className="w-full font-semibold gap-2" disabled={busy || !email}
                style={{ background: NAVY, color: 'white', height: '44px' }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
                Send verification code
              </Button>
            </form>
          </div>
        )}

        {/* ── Step 2: OTP ── */}
        {step === 'otp' && (
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield size={18} style={{ color: NAVY }} />
              <h2 className="font-bold text-lg" style={{ color: NAVY }}>Enter your code</h2>
            </div>
            <p className="text-sm mb-5" style={{ color: '#475569' }}>
              A 6-digit code has been sent to <strong>{email}</strong>. It expires in 10 minutes.
            </p>
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <Input
                type="text" inputMode="numeric" maxLength={6}
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="000000" required
                className="text-center text-3xl tracking-widest font-bold"
                style={{ height: '60px', letterSpacing: '0.3em', color: NAVY }}
              />
              <Button type="submit" className="w-full font-semibold" disabled={busy || otp.length < 6}
                style={{ background: NAVY, color: 'white', height: '44px' }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : 'Verify code'}
              </Button>
              <button type="button" className="w-full text-sm text-center" style={{ color: '#94a3b8' }}
                onClick={() => { setStep('email'); setOtp(''); }}>
                Didn't receive a code? Go back
              </button>
            </form>
          </div>
        )}

        {/* ── Step 3: Sign ── */}
        {step === 'sign' && (
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <div className="flex items-center gap-2 mb-4">
              <PenLine size={18} style={{ color: NAVY }} />
              <h2 className="font-bold text-lg" style={{ color: NAVY }}>Sign your quotation</h2>
            </div>
            <form onSubmit={handleSign} className="space-y-5">
              <div>
                <label className="text-sm font-semibold block mb-1.5" style={{ color: NAVY }}>Full name</label>
                <Input value={signerName} onChange={e => setSignerName(e.target.value)}
                  placeholder="Type your full name" required style={{ height: '44px' }} />
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1.5" style={{ color: NAVY }}>
                  Purchase Order Number <span className="text-xs font-normal" style={{ color: '#94a3b8' }}>(optional)</span>
                </label>
                <Input value={poNumber} onChange={e => setPoNumber(e.target.value)}
                  placeholder="e.g. PO-2026-0042"
                  style={{ height: '44px', fontFamily: 'monospace', letterSpacing: '0.03em' }} />
                <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>
                  If your organisation requires a PO number for this order, enter it here — it will appear on the signed document and contract.
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold block mb-1.5" style={{ color: NAVY }}>Signature</label>
                <SignaturePad signerName={signerName} onChange={setSignatureImg} />
              </div>
              <div className="flex items-start gap-3 rounded-lg p-4" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <input type="checkbox" id="agree" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                  className="mt-1 flex-shrink-0 w-4 h-4 cursor-pointer" style={{ accentColor: NAVY }} />
                <label htmlFor="agree" className="text-sm cursor-pointer" style={{ color: '#475569' }}>
                  I, <strong>{signerName || '[your name]'}</strong>, confirm that I have read and understood
                  this quotation and agree to its terms and pricing. I accept this quotation on behalf of
                  my organisation and authorise Cloud4Partners to proceed accordingly.
                </label>
              </div>
              <Button type="submit" className="w-full font-bold gap-2" disabled={busy}
                style={{ background: ORANGE, color: 'white', height: '48px', fontSize: '15px' }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {quoteInfo?.is_change_request ? 'Accept Contract Change' : 'Accept & Sign Quotation'}
              </Button>
            </form>
            <p className="text-xs text-center mt-4" style={{ color: '#94a3b8' }}>
              Your signature, name, email address, and timestamp will be recorded as proof of acceptance.
            </p>
          </div>
        )}

        <p className="text-xs text-center mt-6" style={{ color: '#94a3b8' }}>
          Secured by Cloud4Partners &trade;
        </p>

        {/* ── GDPR Privacy Notice ── */}
        <GdprNotice controllerName={quoteInfo?.data_controller_name || 'Cloud4Partners'} />
      </div>
    </div>
  );
}

function DonePage({ result, quoteInfo, signerName, signatureImg, poNumber }) {
  const sym = quoteInfo?.currency_symbol || '£';

  useEffect(() => {
    // Confetti celebration
    import('canvas-confetti').then(({ default: confetti }) => {
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.5 }, colors: ['#1e3a5f', '#ea580c', '#22c55e'] });
    });
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: '#f1f5f9' }}>
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <img src={LOGO} alt="Cloud4Partners" className="h-10 mx-auto mb-6 object-contain" />
        </div>
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6 text-center" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a8e 100%)' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: '#22c55e' }}>
              <CheckCircle2 size={32} color="white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">{quoteInfo?.is_change_request ? 'Contract Change Accepted' : 'Quotation Accepted'}</h1>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {result?.quote_number}
            </p>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-sm text-center" style={{ color: '#475569' }}>
              Thank you, <strong style={{ color: NAVY }}>{signerName}</strong>.{' '}
              {quoteInfo?.is_change_request
                ? 'Your contract change has been accepted and your Cloud4Partners representative has been notified.'
                : 'Your acceptance has been recorded and your Cloud4Partners representative has been notified.'}
            </p>
            <div className="rounded-xl p-4 space-y-2" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#16a34a' }}>Signed &amp; Accepted</p>
              <p className="text-sm font-semibold" style={{ color: NAVY }}>{quoteInfo?.quote_name || result?.quote_number}</p>
              {result?.contract_number && (
                <p className="text-xs font-mono font-semibold" style={{ color: NAVY }}>
                  Contract Ref: {result.contract_number}
                </p>
              )}
              {poNumber && poNumber.trim() && (
                <p className="text-xs font-mono font-semibold" style={{ color: '#1d4ed8' }}>
                  PO Number: {poNumber.trim()}
                </p>
              )}
              <p className="text-xs" style={{ color: '#64748b' }}>
                Signed at {(() => {
                  if (!result?.signed_at) return '';
                  const d = new Date(result.signed_at);
                  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                  const day = String(d.getUTCDate()).padStart(2, '0');
                  const month = months[d.getUTCMonth()];
                  const year = d.getUTCFullYear();
                  const hours = String(d.getUTCHours()).padStart(2, '0');
                  const mins = String(d.getUTCMinutes()).padStart(2, '0');
                  return `${day} ${month} ${year} at ${hours}:${mins} UTC`;
                })()}
              </p>
            </div>
            {signatureImg && (
              <div className="rounded-lg p-3 text-center" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <p className="text-xs mb-2" style={{ color: '#94a3b8' }}>Your signature on record</p>
                <img src={signatureImg} alt="Signature" className="mx-auto max-h-16 object-contain" />
              </div>
            )}
            <p className="text-xs text-center" style={{ color: '#94a3b8' }}>
              A binding contract has been formed. A signed copy of this acceptance has been sent to your email address.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
